/**
 * Redemptions Service
 * Handles redemption history with role-based filtering
 *
 * Supports server-side pagination, search, and filtering for scalability
 * with hundreds of thousands of vouchers.
 */

import { supabase } from "./supabase";
import { buildOfferRetailerMap } from "../utils/offerRetailer";
import { csvEscape } from "../utils/segmentCsv";
import {
  validateExportPage,
  type ExportKey,
} from "./redemptionsExportValidation";
import type { Voucher } from "../types/voucher.types";

/**
 * Voucher category for filtering
 */
export type VoucherCategory = "event" | "retail" | "general";

/**
 * Enriched voucher with organization/location display names
 */
export interface EnrichedVoucher extends Voucher {
  organizationName: string;
  locationName: string;
  eventName?: string; // Event name if voucher has event_id
  promoterName?: string; // Promoter name from event (fallback for org/location)
  redemptionLocationName?: string; // Location name where voucher was redeemed (for org-level vouchers with multiple locations)
  voucherSource?: string; // Display source: event name, offer name, or "Subscription Rewards"
  voucherCategory?: VoucherCategory; // Category for filtering: event, retail, or general
}

/**
 * Daily chart data structure for aggregated voucher analytics
 */
export interface DailyChartData {
  date: string;
  issuedValue: number;
  redeemedValue: number;
  redemptionCount: number;
  issuedCount: number;
}

/** The timestamp a Redemptions date range is measured against. */
export type RedemptionDateBasis = "issued" | "redeemed";

export const REDEMPTION_DATE_BASIS_LABELS: Record<RedemptionDateBasis, string> =
  {
    issued: "Issued date",
    redeemed: "Redeemed date",
  };

/**
 * Parameters for paginated redemptions query
 */
export interface PaginatedRedemptionsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: "all" | "redeemed" | "unredeemed" | "expired" | "test" | "cancelled";
  dateFrom?: string;
  dateTo?: string;
  /**
   * Which timestamp the dateFrom/dateTo window applies to.
   *   "issued"   -> vouchers.created_at (the historical, and still default, behaviour)
   *   "redeemed" -> vouchers.redeemed_at
   * Sent to BOTH get_redemptions_page and get_redemptions_export as p_date_basis, so the
   * on-screen list and the CSV can never disagree about what the dates mean.
   */
  dateBasis?: RedemptionDateBasis;
  /**
   * Selected organization identity (UUID). The server matches this against a voucher via ANY of:
   * facility relationships, direct redeeming org, direct redemption-location org, OR the
   * effective-event PROMOTER (events.promoter_id). Replaces the old name->facility-array path,
   * which missed promoter-organized events whose vouchers carry no facility of the org.
   */
  organizationId?: string;
  /** Selected location name; resolved to facility IDs and sent as an AND narrow (p_location_facility_ids). */
  locationName?: string;
  eventId?: string;
  voucherType?: string;
  sortField?: "date" | "value" | "organization" | "status";
  sortDirection?: "asc" | "desc";
  exportAll?: boolean;
  /** Export only: called after each page with (rows fetched so far, expected total). */
  onExportProgress?: (done: number, total: number) => void;
}

/**
 * Result of paginated redemptions query
 */
export interface PaginatedRedemptionsResult {
  data: EnrichedVoucher[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
  /**
   * Name lookups that failed for this page (rows show blank business/location/event names). The page shows a
   * non-blocking notice; the export never gets here with failures (it stops instead).
   */
  nameLookupFailures?: string[];
}

// Shape of location data from Supabase join
interface LocationWithOrganization {
  facility_id: string;
  name: string;
  organization_id: string;
  organizations: { name: string } | null;
}

// Shape of organization data for facility mapping
interface OrganizationFacility {
  facility_id: string;
  name: string;
}

/**
 * Get paginated redemptions with server-side search and filtering
 * Designed for scalability with large datasets (100k+ vouchers)
 */
export async function getRedemptionsPaginated(
  userId?: string,
  userRole?: string,
  params: PaginatedRedemptionsParams = {},
): Promise<PaginatedRedemptionsResult> {
  const {
    page = 1,
    pageSize = 50,
    search = "",
    status = "all",
    dateFrom,
    dateTo,
    dateBasis = "issued",
    organizationId,
    locationName,
    eventId,
    voucherType,
    sortField = "date",
    sortDirection = "desc",
    exportAll = false,
    onExportProgress,
  } = params;

  console.log("[Redemptions Service] getRedemptionsPaginated called", {
    userId,
    userRole,
    page,
    pageSize,
    search,
    status,
  });

  // Organization filter is applied server-side by identity (UUID) via p_org_id — the RPC's
  // voucher_matches_organization() covers facility relationships AND the effective-event
  // promoter, so we no longer resolve the org NAME to a facility array here (that path missed
  // promoter-organized events). Location NARROWS the result and is still resolved to facility
  // IDs, sent separately as p_location_facility_ids (ANDed server-side, never ORed).
  let locationFacilityIds: string[] | null = null;

  if (locationName) {
    let locQuery = supabase
      .from("locations")
      .select("facility_id")
      .eq("name", locationName)
      .not("facility_id", "is", null);
    // Scope the location lookup to the selected org so duplicate location names across orgs
    // don't leak facilities from a different org into the narrow.
    if (organizationId) {
      locQuery = locQuery.eq("organization_id", organizationId);
    }
    const { data: matchingLocations } = await locQuery;
    const locIds = (matchingLocations || [])
      .map((l: any) => l.facility_id)
      .filter(Boolean);
    if (locIds.length === 0) {
      return {
        data: [],
        totalCount: 0,
        page,
        pageSize,
        totalPages: 0,
        hasMore: false,
      };
    }
    locationFacilityIds = locIds;
  }

  // Call server-side RPC — scope derived from auth.uid() inside the function,
  // not from the userId/userRole params passed to this function.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any[];
  let totalCount: number;

  if (exportAll) {
    // ------------------------------------------------------------------------------------
    // HARD EXPORT CEILING (external audit finding 6, 2026-09-12)
    //
    // "Export All" is unbounded: it pulls every matching row into the browser as one JSON
    // array, then duplicates it through enrichment, CSV construction and a Blob. Measured on
    // PROD 2026-09-12 the unfiltered set is ~12,254 rows / ~26.6 MB of raw JSON, so the peak
    // browser footprint is already several multiples of that.
    //
    // Counted BEFORE fetching, deliberately: capping after the fetch would still transfer and
    // parse the whole payload, which is the actual failure mode. The page RPC returns
    // total_count, so a 1-row request is a cheap pre-flight.
    //
    // The audit's instruction was an explicit limit with a VISIBLE error and never silent
    // truncation -- so this throws a message naming the count and the ceiling, and the caller
    // surfaces it. A truncated CSV that looks complete is far worse than a refusal: someone
    // would reconcile finance against it.
    //
    // INTERIM. The real fix is server-side keyset/async CSV generation, which removes the
    // ceiling rather than enforcing it. Tracked separately; do not raise this number as a
    // substitute for that work.
    // ------------------------------------------------------------------------------------
    const EXPORT_ROW_CEILING = 20000;
    const EXPORT_PAGE_SIZE = 1000;

    const { data: countProbe, error: countError } = await supabase.rpc(
      "get_redemptions_page",
      {
        p_page: 1,
        p_page_size: 1,
        p_status: status,
        p_date_from: dateFrom || null,
        p_date_to: dateTo || null,
        p_date_basis: dateBasis,
        p_search: search || null,
        p_event_id: eventId || null,
        p_voucher_type: voucherType || null,
        p_sort_field: sortField || "date",
        p_sort_dir: sortDirection || "desc",
        p_org_id: organizationId || null,
        p_location_facility_ids: locationFacilityIds,
      },
    );

    if (countError) {
      console.error(
        "[Redemptions Service] Export pre-flight count failed:",
        countError,
      );
      throw countError;
    }

    // Fail CLOSED: if the pre-flight cannot establish a count, refuse rather than fall
    // through to an unbounded fetch.
    const probeRow = Array.isArray(countProbe) ? countProbe[0] : countProbe;
    const preflightCount: number | null =
      probeRow && typeof probeRow.total_count === "number"
        ? probeRow.total_count
        : null;

    if (preflightCount === null) {
      throw new Error(
        "Could not determine how many rows this export would contain, so it was not run. " +
          "Please narrow the filters and try again.",
      );
    }

    if (preflightCount > EXPORT_ROW_CEILING) {
      throw new Error(
        `This export would contain ${preflightCount.toLocaleString()} rows, above the ` +
          `${EXPORT_ROW_CEILING.toLocaleString()}-row limit for browser downloads. ` +
          `Narrow the date range or add a filter, then export again. ` +
          `Nothing was downloaded — no partial file was produced.`,
      );
    }

    // Paged export (2026-10-06, migration 371). The single-shot get_redemptions_export returned the whole result
    // as ONE json value (~30 MB unfiltered on PROD); sending it counted against the 8 s statement_timeout, so a
    // full export could time out although the database work is ~1.3 s. Keyset pages (created_at DESC, id DESC)
    // keep each response small. Any page error fails the WHOLE export -- the caller shows the error and no file is
    // produced -- because a CSV missing pages that looks complete is worse than a refusal.
    const exportRows: EnrichedVoucher[] = [];
    const maxPages = Math.ceil(preflightCount / EXPORT_PAGE_SIZE) + 2;
    let afterCreatedAt: string | null = null;
    let afterId: string | null = null;
    // Strict page validation (audit VEP-1): every row well-formed, keys strictly descending within and across
    // pages, and a short page must be confirmed final by an EMPTY next page. Any violation -> no file.
    let lastKey: ExportKey | null = null;
    let shortPageSeen = false;
    for (let pageNo = 1; ; pageNo++) {
      if (pageNo > maxPages) {
        throw new Error(
          `The export did not finish within the expected ${maxPages - 2} pages, so it was stopped. ` +
            "Nothing was downloaded. Please try again.",
        );
      }
      const { data: pageRows, error: pageErr } = await supabase.rpc(
        "get_redemptions_export_page",
        {
          p_status: status,
          p_date_from: dateFrom || null,
          p_date_to: dateTo || null,
          p_date_basis: dateBasis,
          p_search: search || null,
          p_event_id: eventId || null,
          p_voucher_type: voucherType || null,
          p_org_id: organizationId || null,
          p_location_facility_ids: locationFacilityIds,
          p_after_created_at: afterCreatedAt,
          p_after_id: afterId,
          p_limit: EXPORT_PAGE_SIZE,
        },
      );
      if (pageErr) {
        console.error(
          `[Redemptions Service] Export page ${pageNo} failed:`,
          pageErr,
        );
        throw new Error(
          `The export failed on page ${pageNo}. Nothing was downloaded. Please try again.`,
        );
      }
      lastKey = validateExportPage(pageRows, lastKey, pageNo, EXPORT_PAGE_SIZE);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows = pageRows as any[];
      if (rows.length === 0) {
        break; // the end, confirmed
      }
      if (shortPageSeen) {
        throw new Error(
          `The export received more vouchers after a page that should have been the last (page ${pageNo}). ` +
            "Nothing was downloaded. Please try again.",
        );
      }
      const pageFailures: string[] = [];
      exportRows.push(...(await enrichRedemptionRows(rows, pageFailures)));
      if (pageFailures.length > 0) {
        throw new Error(
          `Some names (${Array.from(new Set(pageFailures)).join(", ")}) could not be loaded on page ${pageNo}, ` +
            "so the export was stopped. Nothing was downloaded. Please try again.",
        );
      }
      // Rows deleted mid-export can make the total SMALLER than the count probe (accepted). A LARGER total is
      // unexpected but possible: newly created rows sort above the cursor and are skipped, yet a backdated
      // insert, a row from a transaction that started before the count, or a filter value changing mid-export
      // can still add rows below it. Refuse rather than ship an unexpected result; a retry is cheap.
      if (exportRows.length > preflightCount) {
        throw new Error(
          `The export returned more vouchers (${exportRows.length.toLocaleString()}) than it counted ` +
            `(${preflightCount.toLocaleString()}). Nothing was downloaded. Please try again.`,
        );
      }
      onExportProgress?.(exportRows.length, preflightCount);
      if (rows.length < EXPORT_PAGE_SIZE) {
        shortPageSeen = true; // ask once more; the reply must be empty
      }
      const last = rows[rows.length - 1];
      afterCreatedAt = last.created_at;
      afterId = last.id;
    }
    data = exportRows;
    totalCount = exportRows.length;
  } else {
    const { data: pageData, error: pageError } = await supabase.rpc(
      "get_redemptions_page",
      {
        p_page: page,
        p_page_size: pageSize,
        p_status: status,
        p_date_from: dateFrom || null,
        p_date_to: dateTo || null,
        p_date_basis: dateBasis,
        p_search: search || null,
        p_event_id: eventId || null,
        p_voucher_type: voucherType || null,
        p_sort_field: sortField || "date",
        p_sort_dir: sortDirection || "desc",
        p_org_id: organizationId || null,
        p_location_facility_ids: locationFacilityIds,
      },
    );
    if (pageError) {
      console.error(
        "[Redemptions Service] Error fetching paginated redemptions:",
        pageError,
      );
      throw pageError;
    }
    const response = (pageData as { rows: any[]; total_count: number }) || {
      rows: [],
      total_count: 0,
    };
    data = response.rows || [];
    totalCount = response.total_count || 0;
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  console.log("[Redemptions Service] Paginated query complete", {
    count: data.length,
    totalCount,
    page,
    totalPages,
  });

  // Export rows are enriched page by page inside the export loop (each lookup stays <= one page of ids).
  const nameLookupFailures: string[] = [];
  const enriched: EnrichedVoucher[] = exportAll
    ? (data as EnrichedVoucher[])
    : await enrichRedemptionRows(data, nameLookupFailures);

  // Organization and location filters are now applied at database level (before pagination)
  // No client-side filtering needed

  return {
    data: enriched,
    totalCount,
    page,
    pageSize,
    totalPages,
    hasMore: page < totalPages,
    nameLookupFailures: Array.from(new Set(nameLookupFailures)),
  };
}

/**
 * Resolve organization / location / event / offer names for raw voucher rows from the report RPCs.
 * Shared by the on-screen page and the paged export, so both attribute a voucher identically.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function enrichRedemptionRows(
  data: any[],
  /**
   * Receives the name of every lookup that FAILED (rows that needed it would get blank names). The export treats
   * any entry as fatal ("Nothing was downloaded"); the on-screen list shows a non-blocking notice.
   */
  lookupFailures: string[] = [],
): Promise<EnrichedVoucher[]> {
  const noteFailure = (what: string, error: unknown) => {
    if (error) {
      console.error(`[Redemptions Service] ${what} name lookup failed:`, error);
      lookupFailures.push(what);
    }
  };
  // Enrich vouchers with organization/location names
  const facilityIds = new Set<string>();
  const eventIds = new Set<string>();
  const redemptionLocationIds = new Set<string>();
  const offerIds = new Set<string>();
  const redeemingOrgIds = new Set<string>();

  (data || []).forEach((voucher) => {
    if (voucher.facility_ids && voucher.facility_ids.length > 0) {
      facilityIds.add(voucher.facility_ids[0]);
    }
    // Attribute a redeemed ticket reward (event_id NULL) via the RPC's effective_event_id.
    const evId = voucher.effective_event_id ?? voucher.event_id;
    if (evId) {
      eventIds.add(evId);
    }
    if (voucher.redemption_location_id) {
      redemptionLocationIds.add(voucher.redemption_location_id);
    }
    if (voucher.offer_id) {
      offerIds.add(voucher.offer_id);
    }
    if (voucher.redeeming_organization_id) {
      redeemingOrgIds.add(voucher.redeeming_organization_id);
    }
  });

  // Build lookup maps
  const facilityMap = new Map<
    string,
    { organizationName: string; locationName: string }
  >();

  if (facilityIds.size > 0) {
    const facilityIdArray = Array.from(facilityIds);

    const { data: locations, error: locationsError } = await supabase
      .from("locations")
      .select("facility_id, name, organization_id, organizations(name)")
      .in("facility_id", facilityIdArray);
    noteFailure("voucher facility", locationsError);

    (locations || []).forEach((location: LocationWithOrganization) => {
      facilityMap.set(location.facility_id, {
        organizationName: location.organizations?.name || "Unknown",
        locationName: location.name || "",
      });
    });

    const unmappedFacilityIds = facilityIdArray.filter(
      (id) => !facilityMap.has(id),
    );
    if (unmappedFacilityIds.length > 0) {
      const { data: orgs, error: orgsError } = await supabase
        .from("organizations")
        .select("facility_id, name")
        .in("facility_id", unmappedFacilityIds);
      noteFailure("facility organization", orgsError);

      (orgs || []).forEach((org: OrganizationFacility) => {
        facilityMap.set(org.facility_id, {
          organizationName: org.name || "Unknown",
          locationName: "",
        });
      });
    }
  }

  const eventMap = new Map<
    string,
    {
      title: string;
      promoterName?: string;
      hostOrgName?: string;
      hostLocationName?: string;
    }
  >();
  if (eventIds.size > 0) {
    const eventIdArray = Array.from(eventIds);

    const { data: events, error: eventsError } = await supabase
      .from("events")
      .select(
        `id, title, promoter_name, promoter_id, promoter:organizations!promoter_id(name)`,
      )
      .in("id", eventIdArray);
    noteFailure("event", eventsError);

    (events || []).forEach(
      (event: {
        id: string;
        title: string;
        promoter_name?: string;
        promoter_id?: string;
        promoter?: { name: string } | null;
      }) => {
        const promoterName =
          event.promoter_name || event.promoter?.name || undefined;
        eventMap.set(event.id, { title: event.title, promoterName });
      },
    );

    // The event's HOST (range nights have a host venue, not a promoter). Used only as the
    // Organization/Location fallback for event vouchers that nothing stronger attributes -- e.g. an
    // unredeemed check-in reward -- so filtering the export by the venue shows that event's rewards
    // (2026-09-28: a Medlock range night's rewards vanished under a "Medlock Firearms, LLC" filter).
    // Unambiguous only: one linked organization, or linked venues that all belong to one organization.
    const [
      { data: hostOrgs, error: hostOrgsError },
      { data: hostLocs, error: hostLocsError },
    ] = await Promise.all([
      supabase
        .from("event_organizations")
        .select("event_id, organization:organizations(name)")
        .in("event_id", eventIdArray),
      supabase
        .from("event_locations")
        .select(
          "event_id, location:locations(name, organization:organizations(name))",
        )
        .in("event_id", eventIdArray),
    ]);
    noteFailure("event host organization", hostOrgsError);
    noteFailure("event host location", hostLocsError);
    const orgsByEvent = new Map<string, Set<string>>();
    const locsByEvent = new Map<string, string[]>();
    const add = (m: Map<string, Set<string>>, k: string, v?: string | null) => {
      if (v) {
        m.set(k, (m.get(k) ?? new Set()).add(v));
      }
    };
    (
      (hostOrgs ?? []) as unknown as Array<{
        event_id: string;
        organization: { name: string | null } | null;
      }>
    ).forEach((r) => add(orgsByEvent, r.event_id, r.organization?.name));
    (
      (hostLocs ?? []) as unknown as Array<{
        event_id: string;
        location: {
          name: string | null;
          organization: { name: string | null } | null;
        } | null;
      }>
    ).forEach((r) => {
      add(orgsByEvent, r.event_id, r.location?.organization?.name);
      if (r.location?.name) {
        locsByEvent.set(r.event_id, [
          ...(locsByEvent.get(r.event_id) ?? []),
          r.location.name,
        ]);
      }
    });
    eventMap.forEach((info, id) => {
      const orgs = orgsByEvent.get(id);
      if (orgs && orgs.size === 1) {
        const locs = locsByEvent.get(id) ?? [];
        info.hostOrgName = Array.from(orgs)[0];
        info.hostLocationName = locs.length === 1 ? locs[0] : undefined;
      }
    });
  }

  // Resolve the ACTUAL redemption location -> its name AND its organization. Where a voucher was actually
  // redeemed is the strongest retailer-attribution signal (see the enrichment precedence below), so we need
  // the org, not just the display name.
  const redemptionLocationMap = new Map<
    string,
    { name: string; organizationName: string | null }
  >();
  if (redemptionLocationIds.size > 0) {
    const locationIdArray = Array.from(redemptionLocationIds);

    const { data: redemptionLocations, error: redemptionLocationsError } =
      await supabase
        .from("locations")
        .select("id, name, organizations(name)")
        .in("id", locationIdArray);
    noteFailure("redemption location", redemptionLocationsError);

    (redemptionLocations || []).forEach(
      (location: {
        id: string;
        name: string;
        organizations?: { name?: string } | null;
      }) => {
        redemptionLocationMap.set(location.id, {
          name: location.name,
          organizationName: location.organizations?.name ?? null,
        });
      },
    );
  }

  // Build redeeming organization lookup map for multi-org vouchers
  const redeemingOrgMap = new Map<string, string>();
  if (redeemingOrgIds.size > 0) {
    const redeemingOrgIdArray = Array.from(redeemingOrgIds);

    const { data: redeemingOrgs, error: redeemingOrgsError } = await supabase
      .from("organizations")
      .select("id, name")
      .in("id", redeemingOrgIdArray);
    noteFailure("redeeming organization", redeemingOrgsError);

    (redeemingOrgs || []).forEach((org: { id: string; name: string }) => {
      redeemingOrgMap.set(org.id, org.name);
    });
  }

  // Build offer map for general vouchers
  const offerMap = new Map<string, string>();
  if (offerIds.size > 0) {
    const offerIdArray = Array.from(offerIds);

    const { data: offers, error: offersError } = await supabase
      .from("offers")
      .select("id, name")
      .in("id", offerIdArray);
    noteFailure("offer", offersError);

    (offers || []).forEach((offer: { id: string; name: string }) => {
      offerMap.set(offer.id, offer.name);
    });
  }

  // Offer-based RETAILER fallback: campaign-offer vouchers (offer_id but no voucher-level retailer fields)
  // resolved to a blank org/"Unknown". For SINGLE_ORGANIZATION offers, attribute the configured retailer
  // (used only after actual redemption data — see the enrichment below).
  const offerRetailerMap = await buildOfferRetailerMap(
    Array.from(offerIds),
    (error) => noteFailure("offer retailer", error),
  );

  // Enrich vouchers
  const enriched: EnrichedVoucher[] = (data || []).map((voucher) => {
    let organizationName = "";
    let locationName = "";
    let eventName: string | undefined;
    let promoterName: string | undefined;
    let redemptionLocationName: string | undefined;
    let voucherSource: string | undefined;
    let voucherCategory: VoucherCategory | undefined;

    // Effective event = the voucher's own event, else (for a ticket reward) the token's event.
    const evId = voucher.effective_event_id ?? voucher.event_id;
    if (evId) {
      const eventInfo = eventMap.get(evId);
      if (eventInfo) {
        eventName = eventInfo.title;
        promoterName = eventInfo.promoterName;
      }
    }

    // Retailer attribution precedence — strongest (actual) signal first:
    //   actual redemption location > redeeming org > voucher facility > event promoter > offer fallback.
    // The offer fallback only fills genuinely-unattributed campaign-offer vouchers; actual redemption data
    // always wins so a voucher redeemed at A is never attributed to its pinned/offer org B.

    // 1) Actual redemption LOCATION (also drives the separate "Redemption Location" display column).
    if (voucher.redemption_location_id) {
      const rl = redemptionLocationMap.get(voucher.redemption_location_id);
      if (rl) {
        redemptionLocationName = rl.name;
        if (rl.organizationName) {
          organizationName = rl.organizationName;
          locationName = rl.name;
        }
      }
    }

    // 2) Redeeming organization (actual redemption-time org).
    if (!organizationName && voucher.redeeming_organization_id) {
      const redeemingOrgName = redeemingOrgMap.get(
        voucher.redeeming_organization_id,
      );
      if (redeemingOrgName) {
        organizationName = redeemingOrgName;
      }
    }

    // 3) Voucher facility (the retailer the voucher was pinned to).
    if (
      !organizationName &&
      voucher.facility_ids &&
      voucher.facility_ids.length > 0
    ) {
      const info = facilityMap.get(voucher.facility_ids[0]);
      if (info) {
        organizationName = info.organizationName;
        locationName = info.locationName;
      }
    }

    // 4) Event promoter.
    if (!organizationName && promoterName) {
      organizationName = promoterName;
    }

    // 4b) Event host (range nights): the venue the event was held at.
    if (!organizationName && evId) {
      const host = eventMap.get(evId);
      if (host?.hostOrgName) {
        organizationName = host.hostOrgName;
        locationName = host.hostLocationName ?? "";
      }
    }

    // 5) FALLBACK (last): configured SINGLE_ORGANIZATION offer retailer, only when nothing actual resolved.
    if (!organizationName && voucher.offer_id) {
      const or = offerRetailerMap.get(voucher.offer_id);
      if (or && !or.ambiguous && or.orgName) {
        organizationName = or.orgName;
        if (or.locationName) {
          locationName = or.locationName;
        }
      }
    }

    // Compute voucher source and category
    // Priority: Event > Offer (General) > Retail (Subscription Rewards)
    if (evId && eventName) {
      voucherSource = eventName;
      voucherCategory = "event";
    } else if (voucher.offer_id) {
      const offerName = offerMap.get(voucher.offer_id);
      voucherSource = offerName || "General Voucher";
      voucherCategory = "general";
    } else {
      // Retail/Subscription voucher (facility-based)
      voucherSource = "Subscription Rewards";
      voucherCategory = "retail";
    }

    return {
      ...voucher,
      organizationName,
      locationName,
      eventName,
      promoterName,
      redemptionLocationName,
      voucherSource,
      voucherCategory,
    };
  });

  return enriched;
}

/**
 * Get analytics using optimized aggregate queries
 * Does not fetch all vouchers - uses database-level aggregation
 */
export async function getRedemptionAnalyticsOptimized(
  _userId?: string,
  _userRole?: string,
) {
  console.log("[Redemptions Service] getRedemptionAnalyticsOptimized called");

  // The RPC derives scope internally from auth.uid() — no caller-supplied params.
  // userId/userRole are kept in the signature for API consistency but not used here.
  // Use RPC function to get analytics - bypasses PostgREST 1000 row limit
  const { data, error } = await supabase.rpc("get_voucher_analytics");

  if (error) {
    console.error(
      "[Redemptions Service] Error fetching analytics via RPC:",
      error,
    );
    throw error;
  }

  const analytics = data || {};
  const totalRedemptions = analytics.totalRedemptions || 0;
  const totalValueRedeemed = analytics.totalValueRedeemed || 0;

  return {
    totalVouchers: analytics.totalVouchers || 0,
    totalRedemptions,
    totalUnredeemed: analytics.totalUnredeemed || 0,
    totalIssuedValue: analytics.totalIssuedValue || 0,
    totalValueRedeemed,
    totalUnredeemedValue: analytics.totalUnredeemedValue || 0,
    averageValue:
      totalRedemptions > 0 ? totalValueRedeemed / totalRedemptions : 0,
    totalThisMonth: analytics.totalThisMonth || 0,
    valueThisMonth: analytics.valueThisMonth || 0,
  };
}

/**
 * Get unique filter options for the UI (organizations, voucher types, etc.)
 * Scope is derived server-side via get_redemptions_filter_options() RPC.
 * Partners see only orgs/locations present in their scoped vouchers.
 */
export async function getRedemptionFilterOptions(
  _userId?: string,
  _userRole?: string,
) {
  const voucherCategories: VoucherCategory[] = ["event", "general", "retail"];

  const { data, error } = await supabase.rpc("get_redemptions_filter_options");

  if (error) {
    throw new Error(
      `get_redemptions_filter_options RPC failed: ${error.message}`,
    );
  }

  const result = data as {
    organizations: string[] | null;
    // Additive identity-carrying option list — the org filter now selects by UUID (label = name)
    // so duplicate org names are distinguishable and promoter orgs (no facility) are selectable.
    organizationOptions: { id: string; name: string }[] | null;
    organizationsWithLocations:
      | { organization: string; id: string; locations: string[] }[]
      | null;
  } | null;

  return {
    voucherTypes: voucherCategories,
    organizations: result?.organizations ?? [],
    organizationOptions: result?.organizationOptions ?? [],
    organizationsWithLocations: result?.organizationsWithLocations ?? [],
  };
}

/**
 * Get daily redemption chart data via server-side RPC.
 * If days = 7, centers on current week (Sunday - Saturday).
 * Scope is derived server-side from auth.uid(); userId/userRole are unused.
 */
export async function getDailyRedemptionData(
  _userId?: string,
  _userRole?: string,
  days: number = 7,
) {
  // Compute start date (same logic as before — passed to RPC as p_start_date)
  let startDate: Date;
  if (days === 7) {
    const today = new Date();
    startDate = new Date(today);
    startDate.setDate(today.getDate() - today.getDay()); // Sunday of current week
  } else {
    startDate = new Date();
    startDate.setDate(startDate.getDate() - days + 1);
  }
  const startDateStr = startDate.toISOString().split("T")[0]; // 'YYYY-MM-DD'

  const { data, error } = await supabase.rpc("get_redemptions_daily_chart", {
    p_start_date: startDateStr,
    p_days: days,
  });

  if (error) {
    console.error("[Redemptions Service] Error fetching daily chart:", error);
    throw error;
  }

  // Map snake_case RPC response to camelCase DailyChartData interface
  return ((data as any[]) || []).map(
    (row: any): DailyChartData => ({
      date: row.date,
      redeemedValue: row.redeemed_value ?? 0,
      redemptionCount: row.redeemed_count ?? 0,
      issuedValue: row.issued_value ?? 0,
      issuedCount: row.issued_count ?? 0,
    }),
  );
}

/**
 * Export redemptions to CSV
 */
/**
 * Canonical voucher lifecycle status, matching the Redemptions page badge logic
 * (Redemptions.tsx statusText). Expiry is DATE-derived (end_date < now), so the raw stored
 * `status` (often "active") is not sufficient — using it alone mislabels expired vouchers as
 * Active in the export. Precedence: Test > Cancelled > Redeemed > Expired > Active.
 */
/**
 * The single frontend authority for a voucher's display status — one definition to replace the
 * divergent inline predicates. Mirrors the DB's redeemability rules (redeem_voucher_core /
 * redeem_voucher_guarded): a token-linked event reward that is 'void' or not yet 'assigned' is NOT
 * redeemable even though is_redeemed is false, so it must not read as "Active". Input is a loose,
 * all-optional shape so any voucher-like object (incl. narrowly-typed ones) can pass.
 */
export interface VoucherStatusInput {
  status?: string | null;
  is_cancelled?: boolean | null;
  is_redeemed?: boolean | null;
  token_id?: string | null;
  reward_state?: string | null;
  end_date?: number | null;
}

export function voucherStatusLabel(v: VoucherStatusInput): string {
  if (v.status === "test") {
    return "Test";
  }
  if (v.is_cancelled || v.status === "cancelled") {
    return "Cancelled";
  }
  if (v.is_redeemed) {
    return "Redeemed";
  }
  // Token-linked event reward: reward_state gates redeemability (a $0 placeholder base is 'pending';
  // a revoked reward is 'void'). Neither is "Active".
  if (v.token_id) {
    if (v.reward_state === "void") {
      return "Void";
    }
    if (v.reward_state && v.reward_state !== "assigned") {
      return "Pending";
    }
  }
  if (v.end_date && v.end_date < Date.now()) {
    return "Expired";
  }
  return "Active";
}

export function effectiveVoucherStatus(r: EnrichedVoucher): string {
  return voucherStatusLabel(r);
}

/**
 * A money column: written as a plain number so a legitimate negative or signed amount stays a number in the
 * spreadsheet. Only a finite number qualifies; anything else (a stray string) goes through the text guard.
 */
function csvNumber(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : csvEscape(value);
}

/**
 * The Vouchers report CSV (2026-10-06): RFC-4180 quoting and the spreadsheet formula guard come from the shared
 * csvEscape (segmentCsv.ts) for every TEXT cell -- a leading =, +, -, @, tab or CR is prefixed with '. Money columns
 * (Voucher Value, Applied Amount) are written as plain numbers via csvNumber, deliberately unguarded.
 */
export function buildRedemptionsCsv(redemptions: EnrichedVoucher[]): string {
  const headers = [
    "Code",
    "Status",
    "Voucher Status",
    "Category",
    "Voucher Source",
    "Created Date",
    "Redeemed Date",
    "Business Name",
    "Voucher Value",
    "Applied Amount",
    "Email",
    "Organization/Location",
    "Redemption Location",
    "Event ID",
  ];

  const rows = redemptions.map((r) => {
    // Determine redemption location:
    // - If voucher is NOT redeemed, leave empty
    // - If redeemed and has event, use event name
    // - Otherwise use redemptionLocationName or fallback to locationName/organizationName
    let redemptionLocation = "";
    if (r.is_redeemed) {
      if (r.eventName) {
        // Event-linked vouchers: use event name as redemption location
        redemptionLocation = r.eventName;
      } else {
        // Non-event vouchers: use actual redemption location data
        redemptionLocation =
          r.redemptionLocationName || r.locationName || r.organizationName;
      }
    }

    // Format category for export
    const categoryLabel =
      r.voucherCategory === "event"
        ? "Event"
        : r.voucherCategory === "general"
          ? "General"
          : "Retail";

    // Voucher source - only show for event/general, empty for retail
    const voucherSourceValue =
      r.voucherCategory === "retail" ? "" : r.voucherSource || "";

    return [
      r.voucher_code,
      r.is_redeemed ? "Redeemed" : "Unredeemed",
      effectiveVoucherStatus(r),
      categoryLabel,
      voucherSourceValue,
      r.created_at ? new Date(r.created_at).toLocaleDateString() : "",
      r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : "",
      r.vendor_name || r.booth_id || r.redeemed_by || "",
      { num: Number(r.discount_value) },
      { num: Number(r.applied_amount || r.discount_value) },
      r.email,
      r.locationName || r.organizationName,
      redemptionLocation,
      r.effective_event_id || r.event_id || "",
    ];
  });

  const csvContent = [
    headers.map((h) => csvEscape(h)).join(","),
    ...rows.map((row) =>
      row
        .map((cell) =>
          cell !== null && typeof cell === "object" && "num" in cell
            ? csvNumber((cell as { num: number }).num)
            : csvEscape(cell),
        )
        .join(","),
    ),
  ].join("\n");
  return csvContent;
}

export function exportRedemptionsToCSV(redemptions: EnrichedVoucher[]) {
  const csvContent = buildRedemptionsCsv(redemptions);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `redemptions-${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
